import type { ReactNode } from 'react'
import { Link } from 'react-router'

import type { AlarmRecipient } from '@ctcells/api'
import type { AlarmEvent } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { alarmCategoryLabel, severityLabel, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable } from '../../atoms/DataTable.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { severityBadgeFlat } from '../../atoms/statusTone.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import type { ScreenAccess } from '../access.ts'
import { alarmSourceText, dateTimeText } from '../format.ts'
import { Card, DetailPage, OfflineNotice } from '../resourceParts.tsx'
import { dispatchChannelName, type AlarmDispatch } from '../admin/alarmRecipientsModel.ts'

import { categoryOf } from './alarmCategories.ts'
import {
  ackGate,
  alarmConditionText,
  alarmHeadingOf,
  alarmStateText,
  alarmTitleOf,
  clearGate,
} from './alarmGates.ts'

/**
 * 알람 상세 — **목록 오른쪽 서랍**(eric 2026-09-28 「알람에서도 구조를 오른쪽으로.. 상세 페이지 보이게」).
 *
 * 🔴 **내용은 `Alarm` 응답이 정한다.** 서버가 준 키만 줄로 세우고, 값이 없으면 그 줄을 그리지 않는다
 *    (`—` 로 채운 줄 없음). 실측(2026-09-28 · 84건): `branch`·`device_error`·`correlation_id` 는 한 건도
 *    안 왔다 — 그래도 줄은 «값이 오면» 선다. 키가 없는 것이지 계약에 없는 게 아니다.
 * 🔴 **제목은 알람 ID 다**(eric 2026-09-28 「타이틀에 알람 내용이 보이는데... 그렇게 하지 말고.. id를 타이틀로 하고,
 *    상세 알람 내용은... 그 밑에 상세 페이지 본문쪽에 넣어줘」). 내용 문구는 첫 카드 「요약」의 첫 줄 「내용」이다.
 *    ⚠ ID 는 줄이지 않는다(36자 · 좁으면 `-` 자리에서 접힌다).
 * 🔴 **목록에서 숨을 수 있는 열은 여기 있어야 한다**(eric 같은 날 「목록의 일부 열을 공간이 모자라면 숨겨야지」).
 *    서랍이 열려 목록이 좁아지면 「분류」 → 「모듈」 → 「발생」 이 빠진다(`listColumnPriority.ts`) — 그 값이
 *    갈 곳은 이 서랍뿐이다. 그래서 종전의 「목록 열은 다시 적지 않는다」를 물렸다. 심각도 · 상태는 목록에서
 *    끝까지 남지만, 「요약」 카드가 알람 한 건을 한 자리에서 읽게 같이 적는다(팀장 제안 순서).
 *    「발생」은 날짜까지 적는다 — 목록 칸은 `14:31`(날짜 없음)이다.
 * ⚠ `GET /alarms/{id}` 는 서버에 없다(openapi.json 실측). 서랍은 **목록 응답에서 고른다** — 컨테이너 몫.
 *
 * 🔴 [확인]·[해제]는 여기 머리에 선다(목록 행 끝에서 옮겼다). 판정은 `alarmGates.ts` 하나다.
 *    권한·상태로 못 누르면 **숨기지 않고 비활성 + 사유**. 누른 뒤 화면을 직접 고치지 않는다(낙관적 전이 없음 · DP-04) —
 *    명령 훅이 목록을 무효화하고 서버가 준 값으로 다시 그린다. 자동 해제 없음(DP-02).
 */
export interface AlarmDetailScreenProps {
  /** 서랍에 연 알람. `undefined` = 받은 목록에 그 id 가 없다 */
  alarm: AlarmEvent | undefined
  access?: ScreenAccess
  onClose: () => void
  onAck?: ((alarmId: string) => void) | undefined
  onClear?: ((alarmId: string) => void) | undefined
  /** 확인·해제 요청이 가는 중 — 두 번 누르지 않게 버튼을 비활성으로 둔다 */
  pending?: boolean
  /** 확인·해제 실패 문구(에러 코드 사전 기준). 서랍 안 그 자리에 뜬다 */
  commandError?: string | undefined
  /**
   * 알림 수신자 목록(admin 만 받는다) — 「발송」 칸이 `recipient:<id>` 를 이름으로 바꾼다.
   * `undefined` = 못 받았다(admin 아님 · 아직) → 채널 원문 그대로(「지운 수신자」로 단정하지 않는다)
   */
  recipients?: readonly Pick<AlarmRecipient, 'recipient_id' | 'name'>[] | undefined
}

export function AlarmDetailScreen({
  alarm,
  access = {},
  onClose,
  onAck,
  onClear,
  pending = false,
  commandError,
  recipients,
}: AlarmDetailScreenProps) {
  if (alarm === undefined) {
    return (
      <DetailPage onClose={onClose} title={t('screen.alarmDetail.title')}>
        <EmptyState label={t('state.empty.alarmNotFound')} />
      </DetailPage>
    )
  }

  const inFlight = { disabled: true, disabledReason: t('reason.requestInFlight') } as const
  const ack = pending ? inFlight : ackGate(access, alarm)
  const clear = pending ? inFlight : clearGate(access, alarm)

  return (
    <DetailPage
      actions={
        <span className="btnrow">
          <Button size="pill" variant="primary" {...ack} onClick={() => onAck?.(alarm.alarm_id)}>
            {t('action.ack')}
          </Button>
          {/* ⚠ 스스로 사라지는 알람에는 [해제]를 두지 않는다 — 목록 행 끝에 있던 때와 같은 규칙(`SM-ALM-05`) */}
          {alarm.auto_clear === false ? (
            <Button size="pill" {...clear} onClick={() => onClear?.(alarm.alarm_id)}>
              {t('action.clearAlarm')}
            </Button>
          ) : null}
        </span>
      }
      onClose={onClose}
      /* 🔴 제목 = 알람 ID(eric 2026-09-28). 전체 값 · 좁으면 `-` 자리에서 접힌다 */
      title={
        <span className="mono alarmid" title={alarm.alarm_id}>
          {alarm.alarm_id}
        </span>
      }
    >
      <div className="fstack">
        <OfflineNotice shown={access.offline === true} />
        {commandError === undefined ? null : (
          <p className="deny" role="alert">
            {commandError}
          </p>
        )}
        <Card title={t('card.summary')}>
          <ValueRows rows={summaryRows(alarm)} />
        </Card>
        <Card title={t('card.alarmOccurrence')}>
          <ValueRows rows={occurrenceRows(alarm)} />
        </Card>
        <Card title={t('card.alarmHandling')}>
          <ValueRows rows={handlingRows(alarm)} />
        </Card>
        {/*
          🔵 원격 발송(`Alarm.dispatch` · v0.9.4 · 정본 `07` §4.7). 🔴 키가 없으면 칸째 없다(서버가 말하지 않았다) ·
             빈 배열이면 「발송 기록 없음」(발송 대기는 알람이 처음 만들어질 때만 생긴다) — 둘을 접지 않는다.
        */}
        {alarm.dispatch === undefined ? null : (
          <Card title={t('card.alarmDispatch')}>
            {alarm.dispatch.length === 0 ? (
              <p className="muted">{t('alarmRecipients.dispatch.none')}</p>
            ) : (
              <DispatchTable dispatch={alarm.dispatch} recipients={recipients} />
            )}
          </Card>
        )}
      </div>
    </DetailPage>
  )
}

interface Row {
  key: string
  label: string
  value: ReactNode
}

/** 값이 있는 줄만 남긴다. 🔴 빈 문자열도 «값 없음»이다 — 빈 칸 줄을 세우지 않는다 */
function present(rows: readonly (Row | null)[]): Row[] {
  return rows.filter((row): row is Row => row !== null)
}

function text(
  key: string,
  label: string,
  value: string | null | undefined,
  mono = false,
): Row | null {
  if (value === undefined || value === null || value === '') return null
  return { key, label, value: mono ? <span className="mono">{value}</span> : value }
}

/**
 * 「요약」 — 목록 한 줄이 말하는 것을 한 자리에. 첫 줄이 내용(목록 「알람」 칸의 제목 문구)이다.
 * 🔴 분류 · 모듈 · 발생은 **목록에서 숨을 수 있는 열**이라 여기 없으면 값이 사라진다 — 빼지 마라.
 * ⚠ 분류는 값이 없으면 목록처럼 `—` 다(`alarmCategoryLabel` · 「서버가 안 줬다」도 사실이라 줄을 남긴다).
 */
function summaryRows(alarm: AlarmEvent): Row[] {
  return present([
    { key: 'content', label: t('column.content'), value: alarmHeadingOf(alarm) },
    {
      key: 'severity',
      label: t('column.severity'),
      value: (
        <StatusBadge
          /* 바탕 있는 칩만 테를 걷는다 — 톤 없이 그려 오류 · 긴급(채움)만 flat · 경고 점선 · 정보는 테를 둔다(`severityBadgeFlat`) */
          flat={severityBadgeFlat(alarm.severity ?? 'INFO', undefined)}
          label={severityLabel(alarm.severity ?? 'INFO')}
          status={alarm.severity ?? 'INFO'}
        />
      ),
    },
    {
      key: 'category',
      label: t('column.category'),
      value: alarmCategoryLabel(categoryOf(alarm)),
    },
    moduleRow(alarm),
    { key: 'state', label: t('column.status'), value: alarmStateText(alarm) },
    /* 조건은 상태와 다른 줄이다(eric 2026-10-30 「분리해줘」) — latching 이 아니면 값이 없어 줄이 안 선다 */
    text('condition', t('column.alarmCondition'), alarmConditionText(alarm)),
    text(
      'lastTs',
      t('column.occurredAt'),
      alarm.last_ts ? dateTimeText(alarm.last_ts) : undefined,
      true,
    ),
  ])
}

/**
 * 「모듈」 줄 — 종류(`source` · `alarmSourceText`) 옆에 **그 알람을 만든 사건의 모듈**(`Alarm.module_id` · 계약 0.28.15)로 가는 링크.
 * 🔴 `module_id` 가 없으면(`null` · 옛 판) 종전 그대로 종류 글자만 — 모듈을 `source`·`grouping_key` 에서 지어내지 않는다(`alarmSourceText` 머리말).
 */
function moduleRow(alarm: AlarmEvent): Row | null {
  const moduleId = alarm.module_id
  if (moduleId == null || moduleId === '')
    return text('module', t('column.module'), alarmSourceText(alarm.source))
  return {
    key: 'module',
    label: t('column.module'),
    value: (
      <>
        {alarmSourceText(alarm.source)}{' '}
        <Link className="lnk mono" to={`${PATHS.modules}/${encodeURIComponent(moduleId)}`}>
          {moduleId}
        </Link>
      </>
    ),
  }
}

function occurrenceRows(alarm: AlarmEvent): Row[] {
  const title = alarmTitleOf(alarm)
  const device = alarm.device_error
  const deviceText =
    device === undefined || device === null
      ? undefined
      : [device.driver, device.map, device.code, device.band]
          .filter((v): v is string => typeof v === 'string' && v !== '')
          .join(' · ')
  return present([
    // 🔴 에러 코드 원문 노출은 허용(eric 2026-09-21 「영문 코드 점검 코드 일단 노출하고」)
    text('code', t('row.errorCode'), alarm.error_code, true),
    // 내용 줄이 곧 서버 문구면 다시 쓰지 않는다 — 사전이 덮어쓴 때만 원문이 따로 선다
    title === alarm.message ? null : text('message', t('row.serverMessage'), alarm.message),
    // 발원 «대»는 여기에만 있다(`source` 는 종류) — `alarmSourceText()` 주석
    text('grouping', t('row.groupingKey'), alarm.grouping_key, true),
    text('branch', t('row.branch'), alarm.branch, true),
    text('device', t('row.deviceError'), deviceText, true),
    text('event', t('row.eventId'), alarm.event_id, true),
    text('correlation', t('column.correlationId'), alarm.correlation_id, true),
  ])
}

/**
 * **발송 표** — 한 건이 한 행, 서버 값이 각자 열(eric 2026-10-13 「·」로 이은 한 줄 목록 → 열 표).
 * 열 = `Alarm.dispatch[]` 의 키 그대로: 수신자(`channel` → 이름 · 지운 수신자 · 원문) · 갈래(`kind`) · 상태(`state`) ·
 * 시도(`attempts`) · 보낸 시각(`sent_at`). 🔴 없는 값은 「—」 — 지어내지 않는다.
 */
function DispatchTable({
  dispatch,
  recipients,
}: {
  dispatch: readonly AlarmDispatch[]
  recipients: readonly Pick<AlarmRecipient, 'recipient_id' | 'name'>[] | undefined
}) {
  type DispatchRow = AlarmDispatch & { n: number }
  const rows: DispatchRow[] = dispatch.map((item, index) => ({ ...item, n: index }))
  return (
    <DataTable<DispatchRow>
      caption={t('card.alarmDispatch')}
      columns={[
        {
          key: 'channel',
          header: t('alarmRecipients.dispatch.col.channel'),
          cell: (row) => dispatchChannelName(row.channel, recipients),
          wrapAnywhere: true,
          width: 'fill',
        },
        {
          key: 'kind',
          header: t('column.kind'),
          cell: (row) => t(`alarmRecipients.dispatch.kind.${row.kind}`),
          nowrap: true,
        },
        {
          key: 'state',
          header: t('column.status'),
          cell: (row) => t(`alarmRecipients.dispatch.state.${row.state}`),
          nowrap: true,
        },
        {
          key: 'attempts',
          header: t('alarmRecipients.dispatch.col.attempts'),
          cell: (row) => (row.attempts === undefined ? t('mask.none') : String(row.attempts)),
          nowrap: true,
          align: 'end',
        },
        {
          key: 'sentAt',
          header: t('alarmRecipients.dispatch.col.sentAt'),
          cell: (row) => (row.sent_at ? dateTimeText(row.sent_at) : t('mask.none')),
          nowrap: true,
        },
      ]}
      density="compact"
      rowKey={(row) => `dispatch-${String(row.n)}`}
      rows={rows}
    />
  )
}

function handlingRows(alarm: AlarmEvent): Row[] {
  return present([
    alarm.ack_required === undefined
      ? null
      : {
          key: 'ackRequired',
          label: t('row.ackRequired'),
          value: t(alarm.ack_required ? 'value.yes' : 'value.no'),
        },
    alarm.auto_clear === undefined
      ? null
      : {
          key: 'autoClear',
          label: t('row.autoClear'),
          value: t(alarm.auto_clear ? 'value.yes' : 'value.no'),
        },
    text('ackBy', t('row.ackBy'), alarm.ack_by),
    text('ackAt', t('row.ackAt'), alarm.ack_at ? dateTimeText(alarm.ack_at) : undefined, true),
    text(
      'clearedAt',
      t('row.clearedAt'),
      alarm.cleared_at ? dateTimeText(alarm.cleared_at) : undefined,
      true,
    ),
  ])
}
